import { useEffect, useMemo } from 'react';
import { UserKind } from 'src/api/generated';
import type { GuidedHandoffEmployee } from 'src/modules/automations/types/quick-automation.type';
import { useEmployees } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { useFunnel } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { UserPermission } from 'src/modules/shared/types/permissions.type';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';

const MAX_PAGE_SIZE = 500;
const EMPLOYEE_PAGE_SIZE = 100;

/**
 * Loads the complete tenant data required by the guided handoff form.
 *
 * All cursor pages are consumed before `loading` becomes false so defaults are
 * never calculated from a partial funnel or AI-Agent list.
 *
 * @param funnelId - Funnel currently displayed in Communications.
 * @param enabled - Whether the guided form is open and allowed to query data.
 * @returns Funnel, statuses, AI Agents linked to system users, and aggregate request state.
 */
export const useGuidedHandoffSetup = (
  funnelId: number | undefined,
  enabled: boolean
) => {
  const queryEnabled = enabled && typeof funnelId === 'number';
  const statusQuery = useMemo(
    () =>
      createQuery()
        .equals('funnelId', funnelId ?? 0)
        .orderByAsc('position')
        .take(MAX_PAGE_SIZE)
        .build(),
    [funnelId]
  );
  const systemUserFilters = useMemo(
    () => ({
      filterBy: createQuery().equals('kind', UserKind.SYSTEM).build().filterBy,
    }),
    []
  );
  const { hasPermission: canReadUsers, loading: usersPermissionLoading } =
    useHasPermissions(UserPermission.Read);
  const usersQueryEnabled = enabled && canReadUsers && !usersPermissionLoading;

  const {
    funnel,
    loading: funnelLoading,
    error: funnelError,
    refetch: refetchFunnel,
  } = useFunnel({
    id: funnelId ?? 0,
    enabled: queryEnabled,
  });
  const {
    statuses,
    loading: statusesLoading,
    error: statusesError,
    fetchNextPage: fetchNextStatusPage,
    hasNextPage: hasNextStatusPage,
    isFetchingNextPage: isFetchingNextStatusPage,
    refetch: refetchStatuses,
  } = useFunnelStatuses(statusQuery, queryEnabled);
  const {
    employees,
    loading: employeesLoading,
    error: employeesError,
    fetchNextPage: fetchNextEmployeePage,
    hasNextPage: hasNextEmployeePage,
    isFetchingNextPage: isFetchingNextEmployeePage,
    refetch: refetchEmployees,
  } = useEmployees(EMPLOYEE_PAGE_SIZE, enabled);
  const {
    users,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useAllUsers(systemUserFilters, usersQueryEnabled);

  useEffect(() => {
    if (
      enabled &&
      !statusesError &&
      hasNextStatusPage &&
      !isFetchingNextStatusPage
    ) {
      fetchNextStatusPage();
    }
  }, [
    enabled,
    fetchNextStatusPage,
    hasNextStatusPage,
    isFetchingNextStatusPage,
    statusesError,
  ]);

  useEffect(() => {
    if (
      enabled &&
      !employeesError &&
      hasNextEmployeePage &&
      !isFetchingNextEmployeePage
    ) {
      fetchNextEmployeePage();
    }
  }, [
    employeesError,
    enabled,
    fetchNextEmployeePage,
    hasNextEmployeePage,
    isFetchingNextEmployeePage,
  ]);

  const eligibleEmployees = useMemo<GuidedHandoffEmployee[]>(() => {
    const systemUsersById = new Map(
      users
        .filter((user) => user.kind === UserKind.SYSTEM)
        .map((user) => [user.id, user])
    );

    return employees.flatMap((employee) => {
      if (typeof employee.userId !== 'number') {
        return [];
      }

      const user = systemUsersById.get(employee.userId);
      if (!user) {
        return [];
      }

      return [
        {
          ...employee,
          userDisplayName: formatFullName(
            user.firstName,
            user.lastName,
            user.email
          ),
        },
      ];
    });
  }, [employees, users]);
  const loading =
    enabled &&
    (usersPermissionLoading ||
      funnelLoading ||
      statusesLoading ||
      employeesLoading ||
      (canReadUsers && usersLoading) ||
      hasNextStatusPage ||
      isFetchingNextStatusPage ||
      hasNextEmployeePage ||
      isFetchingNextEmployeePage);
  const error =
    funnelError ??
    statusesError ??
    employeesError ??
    (canReadUsers ? usersError : null);

  const refetch = async () => {
    await Promise.all([
      refetchFunnel(),
      refetchStatuses(),
      refetchEmployees(),
      ...(canReadUsers ? [refetchUsers()] : []),
    ]);
  };

  return {
    funnel,
    statuses,
    employees: eligibleEmployees,
    canReadUsers,
    usersPermissionLoading,
    loading,
    error,
    refetch,
  };
};
